import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import './WalkInFormEnquiry.css';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from "../context/UserContext";
import axios from 'axios';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const WalkInFormEnquiry = () => {
  const navigate = useNavigate();
  const { selectedCenter, userData } = useUser();
  const token = localStorage.getItem('auth-token');

  const [formData, setFormData] = useState({
    centre: null,
    name: '',
    email: '',
    phone: '',
    sport: null,
    enquiryFor: null,
    comments: ''
  });

  const [options, setOptions] = useState({
    centreOptions: [],
    sportOptions: [],
    enquiryOptions: []
  });

  const baselineRef = useRef(null);

  const buildSubmitPayload = (fd = formData) => ({
    academyId: Number(fd.centre?.value || '') || null,
    name: fd.name || '',
    mobileNumber: fd.phone || '',
    sportId: Number(fd.sport?.value || '') || null,
    enquiryFor: fd.enquiryFor?.value || '',
    enquiry: fd.comments || '',
    email: fd.email || ''
  });

  const fetchOptions = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        }
      });

      const academies = response.data?.data?.academies || [];
      let filtered = academies;
      if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
        filtered = academies.filter(a => userData.academyIds.includes(a.id));
      }

      const centreOptions = filtered.map(a => ({
        value: String(a.id),
        label: a.name,
        sports: a.sports || []
      }));

      const enquiryResponse = await axios.get(process.env.REACT_APP_API_URL_WALK_POST, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const enquiryOptions = (enquiryResponse.data?.data?.EnquiryFor || []).map(enq => ({
        value: enq,
        label: enq,
      }));

      setOptions(prev => ({ ...prev, centreOptions, enquiryOptions }));

      if (selectedCenter) {
        const defCentre = centreOptions.find(opt => opt.value === String(selectedCenter));
        if (defCentre) {
          const sportOptions = (defCentre.sports || []).map(s => ({
            label: s.name,
            value: String(s.sport_id),
          }));
          setOptions(prev => ({ ...prev, sportOptions }));
          setFormData(prev => ({
            ...prev,
            centre: defCentre,
            sport: sportOptions.length === 1 ? sportOptions[0] : null,
          }));
        }
      }
      baselineRef.current = buildSubmitPayload({
        ...formData,
        centre: selectedCenter
          ? centreOptions.find(opt => opt.value === String(selectedCenter)) || null
          : null,
        sport: null,
        enquiryFor: null,
        comments: '',
        name: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (selectedCenter && options.centreOptions.length > 0) {
      const matchingOption = options.centreOptions.find(opt => opt.value === String(selectedCenter));
      if (matchingOption) {
        const sportOptions = (matchingOption.sports || []).map(s => ({
          label: s.name,
          value: String(s.sport_id),
        }));
        setOptions(prev => ({ ...prev, sportOptions }));
        setFormData(prev => ({
          ...prev,
          centre: matchingOption,
          sport: sportOptions.length === 1 ? sportOptions[0] : null,
        }));
      }
    }
  }, [selectedCenter, options.centreOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phone: onlyDigits }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    if (actionMeta.name === 'centre') {
      if (!selectedOption) {
        setOptions(prev => ({ ...prev, sportOptions: [] }));
        setFormData(prev => ({ ...prev, centre: null, sport: null }));
        return;
      }
      const sportOptions = (selectedOption.sports || []).map(s => ({
        label: s.name,
        value: String(s.sport_id),
      }));
      setOptions(prev => ({ ...prev, sportOptions }));
      setFormData(prev => ({
        ...prev,
        centre: selectedOption,
        sport: sportOptions.length === 1 ? sportOptions[0] : null,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [actionMeta.name]: selectedOption
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.phone)) {
      Swal.fire({
        title: 'Error!',
        text: 'Please enter a valid mobile number (10 digits).',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const previewPayload = buildSubmitPayload(formData);
    const oldPayload = baselineRef.current || {};

    const { digest, html } = buildDiffPreview(oldPayload, previewPayload);
    const confirm = await Swal.fire({
      title: 'Submit this enquiry?',
      html: digest.length ? html : '<div style="text-align:left;font-size:13px">Submitting initial enquiry.</div>',
      showCancelButton: true,
      confirmButtonText: 'Submit',
    });
    if (!confirm.isConfirmed) return;

    const body = {
      ...previewPayload,
      createdAt: new Date().toISOString(),
    };

    try {
      await axios.post(process.env.REACT_APP_API_URL_WALK_POST, body, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      fireAudit({
        entity: 'walkin_enquiry',
        affected_id: null,
        action: 'create',
        businessApi: process.env.REACT_APP_API_URL_WALK_POST,
        businessMethod: 'POST',
        oldPayload,
        newPayload: previewPayload,
        section: 'Centre Management',
        subSection: 'WalkIn Enquiry Form',
        extra: { page: 'WalkInFormEnquiry' },
      });

      Swal.fire({
        title: 'Success!',
        text: 'Form submitted successfully.',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = previewPayload;

      navigate('/walkinenquiry');
    } catch (error) {
      console.error('Error submitting form:', error);
      Swal.fire({
        title: 'Error!',
        text: error?.response?.data?.message || 'There was an error submitting the form.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/walkinenquiry" className="breadcrumb-link">Enquiries</Link> &gt; New Enquiry
      </div>

      <form onSubmit={handleSubmit} className="walkin-enquiry-form">
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="centre"
            options={options.centreOptions}
            value={formData.centre}
            onChange={handleSelectChange}
            placeholder="Select Centre"
            className={`walkin-form-dropdown ${formData.centre ? "has-value" : ""}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {formData.centre && <label htmlFor="centre">Centre</label>}
        </div>

        <div className="floating-select-form" style={{ width: '100%' }}>
          <label>Sport</label>
          <Select
            name="sport"
            options={options.sportOptions}
            value={formData.sport}
            onChange={handleSelectChange}
            placeholder="Select Sport"
            className={`walkin-form-dropdown ${formData.sport ? "has-value" : ""}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {formData.sport && <label htmlFor="sport">Sport</label>}
        </div>

        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="enquiryFor"
            options={options.enquiryOptions}
            value={formData.enquiryFor}
            onChange={handleSelectChange}
            placeholder="Select Enquiry Type"
            className={`walkin-form-dropdown ${formData.enquiryFor ? "has-value" : ""}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {formData.enquiryFor && <label htmlFor="enquiryFor">Enquiry For</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="name"
            className={`walkin-form ${formData.name ? "has-value" : ""}`}
            value={formData.name}
            onChange={handleChange}
            placeholder='Enter Name'
          />
          {formData.name && <label htmlFor="name">Name</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="email"
            name="email"
            className={`walkin-form ${formData.email ? "has-value" : ""}`}
            value={formData.email}
            onChange={handleChange}
            placeholder='Enter Email (Optional)'
          />
          {formData.email && <label htmlFor="email">Email</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="tel"
            name="phone"
            className={`walkin-form ${formData.phone ? "has-value" : ""}`}
            value={formData.phone}
            onChange={handleChange}
            placeholder='Enter Phone Number'
            maxLength={10}
          />
          {formData.phone && <label htmlFor="phone">Phone</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            name="comments"
            className={`walkin-form ${formData.comments ? "has-value" : ""}`}
            value={formData.comments}
            onChange={handleChange}
            placeholder='Comments (Optional)'
          />
          {formData.comments && <label htmlFor="comments">Comments</label>}
        </div>

        <button type="submit" className="submit-button">
          Submit
        </button>
      </form>
    </div>
  );
};

export default WalkInFormEnquiry;
